import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, Inject, makeStateKey, OnInit, PLATFORM_ID, TransferState } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { AuthorsApiService } from '@modules/authors/authors-api.service';
import { BucketS3Service } from '@shared/services/bucket-s3.service';
import { SeoMetadataService } from '@shared/services/seo-metadata.service';
import { tap } from 'rxjs';

const AUTHORS_KEY = makeStateKey<any>('AUTHORS');

@Component({
  selector: 'astrea-authors',
  templateUrl: './authors.component.html',
  styleUrls: ['./authors.component.scss']
})
export class AuthorsComponent implements OnInit {

  public alphabet = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

  public authors!: any[];
  public loading = true;
  public totalLength!: number;
  public itemsPerPage: number = 32;
  public filters = {
    letter: 'A',
    author: '',
    limit: this.itemsPerPage,
    offset: 0
  };

  public formSearch = this.formBuilder.group({
    author: [null, Validators.required],
  });

  public firstLoad = true;

  constructor(
    public s3Service: BucketS3Service,
    private formBuilder: UntypedFormBuilder,
    private seo: SeoMetadataService,
    private route: ActivatedRoute,
    private api: AuthorsApiService,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  ngOnInit(): void {
    this.setMetadata();

    const letter = this.route.snapshot.queryParams['letter'];
    this.filters.letter = letter || 'A';

    const page = this.route.snapshot.queryParams['page'];
    this.filters.offset = page ? (parseInt(page) - 1) * this.itemsPerPage : 0;

    this.loadAuthors();

    this.route.queryParams
      .subscribe({
        next: (params: Params) => {
          if (isPlatformBrowser(this.platformId)) {
            this.filters.letter = params.letter ? params.letter : 'A';
            this.filters.offset = params.page ? (params.page-1)*this.itemsPerPage : 0;

            if (this.firstLoad) {
              this.firstLoad = false;
              return;
            }

            this.loadAuthors();
          }
        }
      });
  }

  setMetadata(): void {
    this.seo.setMetadata({
      title: 'Nuestros autores',
      description: 'La editorial Astrea cuenta con obras de más de 7000 autores nacionales e internacionales.',
      relativeUrl: '/authors'
    });
  }

  getSearch(): void {
    this.filters.letter = '';
    this.filters.author = this.formSearch.get('author')?.value;
    this.getAuthors();
  }

  resetSearcher(): void {
    this.filters.letter = 'A';
    this.getAuthors(true);
  }

  loadAuthors(): void {
    const cachedData = this.transferState.get(AUTHORS_KEY, null);
    if (cachedData) {
      this.authors = cachedData.result.data;
      this.totalLength = cachedData.result.total;
      this.loading = false;
      this.transferState.remove(AUTHORS_KEY);
    } else {
      this.getAuthors(true);
    }
  }

  getAuthors(reset = false): void {
    this.loading = true;
    if (reset) {
      this.filters.author = '';
      this.formSearch.patchValue({ author: '' });
    }
    this.api.getAuthors(this.filters)
      .pipe(
        tap(data => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(AUTHORS_KEY, data);
          }
        })
      )
      .subscribe({
        next: res => {
          this.authors = res.result.data;
          this.totalLength = res.result.total;
          this.loading = false;
        }
      });
  }

}
